// StatsResults.jsx
import React, { useState, useEffect, useMemo } from 'react';
import api from '../api/axiosConfig';
import {
  Box,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Avatar,
  Typography,
  TablePagination,
  Card,
  CardHeader,
  CardContent,
  Grid,
  TableSortLabel,
  Tooltip,
  Link,
  useMediaQuery,
  IconButton,
  TextField,
} from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import { useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { usePlayerComparison } from '../context/PlayerComparisonContext';
import { useClubTheme } from '../contexts/ClubThemeContext';
import { usePositions } from '../utils/positions';
import PlayerCompareBar from './shared/PlayerCompareBar';
import PlayerCompareDialog from './shared/PlayerCompareDialog';
import PlayerRankingChip from './shared/PlayerRankingChip';
import {
  makeHeaderSx,
  makeCellSx,
  tableContainerSx,
  tableSx,
  DESKTOP_AVATAR_SIZE,
} from './shared/tableStyles';

/** Couleurs pour les plages de stats — palette pastel harmonisée */
const colorMapping = {
  darkGreen: '#66BB6A',
  lightGreen: '#C8E6C9',
  yellow: '#FFF59D',
  lightOrange: '#FFCC80',
  darkRed: '#EF9A9A',
};

/** Position => correspondance vers clé de position */
const positionMappings = {
  pointGuard: ['Base', 'Point Guard', 'PG', 'Meneur'],
  shootingGuard: ['Escolta', 'Shooting Guard', 'Guard', 'SG', 'Arrière'],
  smallForward: ['Alero', 'Small Forward', 'Swingman', 'SF', 'Ailier'],
  powerForward: ['Ala-Pívot', 'Ala-Pivot', 'Power Forward', 'Power Forward / Center', 'PF', 'Ailier fort'],
  center: ['Pívot', 'Pivot', 'Center', 'C'],
};

function unifyPosition(rawPosition) {
  if (!rawPosition) return rawPosition;
  for (const [canonicalPos, synonyms] of Object.entries(positionMappings)) {
    if (synonyms.includes(rawPosition)) return canonicalPos;
  }
  return rawPosition;
}

const StatsResults = ({
  filteredPlayers,
  page = 0,
  rowsPerPage = 25,
  onPageChange = () => {},
  onRowsPerPageChange = () => {},
  mediaUrl = '',
  timestamp = '',
  isMobile = false,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const { primaryColor: clubPrimaryColor } = useClubTheme();
  const { translateFromDB } = usePositions(t);

  const headerSx = makeHeaderSx(clubPrimaryColor);
  const cellSx = makeCellSx;

  // Comparaison
  const {
    compareMode,
    playersToCompare,
    originalPlayers,
    effectivePlayers,
    togglePlayerComparison,
    isPlayerSelected,
    enterCompareMode,
    exitCompareMode,
    clearComparison,
  } = usePlayerComparison();

  // Dialog comparaison
  const [compareDialogOpen, setCompareDialogOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  const [minGames, setMinGames] = useState('');

  const handleOpenCompareDialog = () => {
    setCompareDialogOpen(true);
    setSearchResults(originalPlayers);
  };
  const handleCloseCompareDialog = () => {
    setCompareDialogOpen(false);
    setSearchText('');
    setSearchResults(originalPlayers);
    clearComparison();
  };

  useEffect(() => {
    if (searchText.length < 3) {
      setSearchResults(originalPlayers);
      return;
    }
    const lower = searchText.toLowerCase();
    setSearchResults(originalPlayers.filter((p) => p.nom.toLowerCase().includes(lower)));
  }, [searchText, originalPlayers]);

  const handleValidateCompare = () => {
    if (playersToCompare.length < 2) return;
    enterCompareMode();
    setCompareDialogOpen(false);
  };

  // Color ranges
  const [colorRanges, setColorRanges] = useState([]);
  useEffect(() => {
    const fetchColorRanges = async () => {
      try {
        const response = await api.get('/api/color-ranges');
        setColorRanges(response.data);
      } catch (err) {
        console.error('Erreur récupération color_ranges :', err);
      }
    };
    fetchColorRanges();
  }, []);

  function getBackgroundColor(metric, value, position) {
    if (!value || !metric || !position) return 'inherit';
    if (colorRanges.length === 0) return 'inherit';

    const numericValue = parseFloat(value);
    if (isNaN(numericValue)) return 'inherit';

    const ranges = colorRanges.filter(
      (range) => range.position === position && range.metric === metric
    );
    for (const range of ranges) {
      const minVal = range.min_value !== null ? parseFloat(range.min_value) : null;
      const maxVal = range.max_value !== null ? parseFloat(range.max_value) : null;
      const aboveMin = minVal === null || numericValue >= minVal;
      const belowMax = maxVal === null || numericValue <= maxVal;
      if (aboveMin && belowMax) {
        return colorMapping[range.color_label] || 'inherit';
      }
    }
    return 'inherit';
  }

  // Aplatir les stats, filtrer par minGames + trier par nombre de matchs
  const allStats = useMemo(() => {
    return effectivePlayers.flatMap((player) => {
      let parsedStats = [];
      if (player.stats) {
        try {
          if (Array.isArray(player.stats)) parsedStats = player.stats;
          else if (typeof player.stats === 'string') parsedStats = JSON.parse(player.stats);
          else if (typeof player.stats === 'object' && Array.isArray(player.stats)) {
            parsedStats = player.stats;
          }
        } catch (error) {
          console.error('Erreur de parsing stats pour le joueur', player.id, error);
          parsedStats = [];
        }
      }

      const avatarUrl = `${mediaUrl}/photos/${player.id}.png?${timestamp}`;
      const position = unifyPosition(player.poste);

      const filteredLines = (parsedStats || []).filter((statLine) => {
        const g = parseFloat(statLine.games);
        const minGamesNum = minGames === '' ? 0 : parseInt(minGames, 10) || 0;
        if (isNaN(g)) return minGamesNum <= 0;
        return g >= minGamesNum;
      });

      filteredLines.sort((a, b) => (parseFloat(b.games) || 0) - (parseFloat(a.games) || 0));

      if (filteredLines.length === 0) {
        return [
          {
            playerId: player.id,
            playerName: player.nom,
            avatarUrl,
            position,
            season_year: null,
            competition_name: null,
            games: null,
            points: null,
            rebounds_off: null,
            rebounds_def: null,
            total_rebounds: null,
            assists: null,
            steals: null,
            blocks: null,
            turnovers: null,
            personal_fouls: null,
            minutes: null,
            ranking: null,
            two_fg_percent: null,
            three_fg_percent: null,
            free_throw_percent: null,
          },
        ];
      }

      return filteredLines.map((statLine) => ({
        playerId: player.id,
        playerName: player.nom,
        avatarUrl,
        position,
        ...statLine,
      }));
    });
  }, [effectivePlayers, mediaUrl, timestamp, minGames]);

  // Tri global
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('');

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  function descendingComparator(a, b, key) {
    const valA = a[key] ?? '';
    const valB = b[key] ?? '';
    const numA = parseFloat(valA);
    const numB = parseFloat(valB);
    if (!isNaN(numA) && !isNaN(numB)) {
      if (numB < numA) return -1;
      if (numB > numA) return 1;
      return 0;
    }
    if (valB < valA) return -1;
    if (valB > valA) return 1;
    return 0;
  }
  function getComparator(o, key) {
    return o === 'desc'
      ? (a, b) => descendingComparator(a, b, key)
      : (a, b) => -descendingComparator(a, b, key);
  }
  function stableSort(array, comparator) {
    const stabilized = array.map((el, idx) => [el, idx]);
    stabilized.sort((a, b) => {
      const cmp = comparator(a[0], b[0]);
      if (cmp !== 0) return cmp;
      return a[1] - b[1];
    });
    return stabilized.map((el) => el[0]);
  }

  let sortedStats = allStats;
  if (orderBy) sortedStats = stableSort(allStats, getComparator(order, orderBy));

  // Colonnes
  const columns = [
    { id: 'playerName', label: t('statsResults.table.player'), width: 150, sortDisabled: true },
    { id: 'position', label: t('statsResults.table.position'), width: 35, sortDisabled: true },
    { id: 'competition_name', label: t('statsResults.table.competition'), width: 120 },
    { id: 'games', label: t('statsResults.table.games'), width: 30 },
    { id: 'minutes', label: t('statsResults.table.minutes'), width: 35 },
    { id: 'points', label: t('statsResults.table.points'), width: 30 },
    { id: 'two_fg_percent', label: t('statsResults.table.twoPercent'), width: 35 },
    { id: 'three_fg_percent', label: t('statsResults.table.threePercent'), width: 35 },
    { id: 'free_throw_percent', label: t('statsResults.table.freeThrowPercent'), width: 35 },
    { id: 'rebounds_off', label: t('statsResults.table.offRebounds'), width: 30 },
    { id: 'rebounds_def', label: t('statsResults.table.defRebounds'), width: 30 },
    { id: 'total_rebounds', label: t('statsResults.table.totalRebounds'), width: 30 },
    { id: 'assists', label: t('statsResults.table.assists'), width: 30 },
    { id: 'steals', label: t('statsResults.table.steals'), width: 30 },
    { id: 'turnovers', label: t('statsResults.table.turnovers'), width: 30 },
    { id: 'blocks', label: t('statsResults.table.blocks'), width: 30 },
    { id: 'personal_fouls', label: t('statsResults.table.fouls'), width: 30 },
    { id: 'ranking', label: t('statsResults.table.rating'), width: 30 },
  ];

  const uniquePlayersCount = effectivePlayers.length;
  const totalCount = sortedStats.length;
  const isGroupingMode = !orderBy || orderBy === 'playerName';

  // Regroupement par joueur
  const statsByPlayer = useMemo(() => {
    const map = new Map();
    for (const row of sortedStats) {
      if (!map.has(row.playerId)) map.set(row.playerId, []);
      map.get(row.playerId).push(row);
    }
    return Array.from(map, ([playerId, lines]) => ({
      playerId,
      playerName: lines[0]?.playerName ?? '',
      avatarUrl: lines[0]?.avatarUrl ?? '',
      position: lines[0]?.position ?? '',
      lines,
    }));
  }, [sortedStats]);

  // Accordéon
  const [expandedPlayers, setExpandedPlayers] = useState({});
  const handleToggleExpand = (playerId) => {
    setExpandedPlayers((prev) => ({ ...prev, [playerId]: !prev[playerId] }));
  };

  // -------------------------------------------------------------------------
  // ÉLÉMENTS PARTAGÉS (header + dialog)
  // -------------------------------------------------------------------------
  const compareDialogLabels = {
    title: t('statsResults.comparison.title'),
    searchPlaceholder: t('statsResults.comparison.searchPlaceholder'),
    selectedSingular: t('statsResults.comparison.selectedSingular'),
    selectedPlural: t('statsResults.comparison.selectedPlural'),
    cancel: t('statsResults.buttons.cancel'),
    validate: t('statsResults.buttons.validate'),
  };

  const compareDialog = (
    <PlayerCompareDialog
      open={compareDialogOpen}
      onClose={handleCloseCompareDialog}
      onValidate={handleValidateCompare}
      searchText={searchText}
      setSearchText={setSearchText}
      searchResults={searchResults}
      playersToCompare={playersToCompare}
      togglePlayerComparison={togglePlayerComparison}
      isPlayerSelected={isPlayerSelected}
      mediaUrl={mediaUrl}
      timestamp={timestamp}
      labels={compareDialogLabels}
    />
  );

  const minGamesField = (
    <TextField
      type="number"
      label={t('statsResults.filters.minGames')}
      variant="outlined"
      size="small"
      value={minGames}
      onChange={(e) => {
        const value = e.target.value;
        if (value === '') {
          setMinGames('');
        } else {
          const num = parseInt(value, 10);
          if (!isNaN(num) && num >= 0) setMinGames(num);
        }
      }}
      onBlur={() => {
        if (minGames === '') setMinGames(0);
      }}
      inputProps={{ min: 0 }}
      sx={{ width: 160 }}
    />
  );

  const renderCompareBar = (dense = false) => (
    <PlayerCompareBar
      count={uniquePlayersCount}
      countLabel={
        uniquePlayersCount !== 1
          ? t('statsResults.counter.playerPlural')
          : t('statsResults.counter.playerSingular')
      }
      compareMode={compareMode}
      onOpenCompare={handleOpenCompareDialog}
      onExitCompare={exitCompareMode}
      compareLabel={t('statsResults.buttons.compare')}
      backLabel={t('statsResults.buttons.back')}
      dense={dense}
    >
      {minGamesField}
    </PlayerCompareBar>
  );

  // -------------------------------------------------------------------------
  // MOBILE / PETIT ÉCRAN
  // -------------------------------------------------------------------------
  if (isMobile || isSmallScreen) {
    const paginatedStats = sortedStats.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );

    return (
      <Box>
        {renderCompareBar(true)}

        {paginatedStats.map((row, i) => (
          <Card
            key={`${row.playerId}-${i}`}
            sx={{
              mb: 2,
              borderRadius: 2,
              boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
            }}
          >
            <CardHeader
              avatar={
                <Avatar
                  src={row.avatarUrl}
                  alt={row.playerName}
                  sx={{ width: 56, height: 56 }}
                />
              }
              title={
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  {row.playerName}
                </Typography>
              }
              subheader={
                <Typography variant="caption" color="textSecondary">
                  {row.position ? translateFromDB(row.position) : '-'}
                </Typography>
              }
            />
            <CardContent sx={{ pt: 0 }}>
              <Grid container spacing={1}>
                {columns.map((col) => {
                  if (col.id === 'playerName' || col.id === 'position') return null;
                  const value = row[col.id];
                  const bg = getBackgroundColor(col.id, value, row.position);
                  const displayed = value != null ? value : '-';
                  return (
                    <Grid
                      item
                      xs={6}
                      key={col.id}
                      sx={{
                        backgroundColor: bg,
                        textAlign: 'center',
                        borderRadius: 1,
                        py: 0.5,
                      }}
                    >
                      <Typography variant="caption" sx={{ fontWeight: 700, opacity: 0.7 }}>
                        {col.label}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {displayed}
                      </Typography>
                    </Grid>
                  );
                })}
              </Grid>
            </CardContent>
          </Card>
        ))}

        <TablePagination
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage=""
          labelDisplayedRows={({ from, to, count }) =>
            t('statsResults.pagination.displayedRows', { from, to, count })
          }
        />

        {compareDialog}
      </Box>
    );
  }

  // -------------------------------------------------------------------------
  // DESKTOP — Header de table
  // -------------------------------------------------------------------------
  const DesktopTableHeader = () => (
    <TableHead>
      <TableRow>
        <TableCell
          sx={{ ...headerSx(220), fontWeight: 'bold' }}
          sortDirection={orderBy === 'playerName' ? order : false}
          align="center"
        >
          <TableSortLabel
            active={orderBy === 'playerName'}
            direction={orderBy === 'playerName' ? order : 'asc'}
            onClick={() => handleRequestSort('playerName')}
            sx={{
              fontSize: '0.72rem',
              color: 'white !important',
              '& .MuiTableSortLabel-icon': {
                color: 'white !important',
                opacity: 0.6,
                visibility: 'visible !important',
              },
            }}
          >
            {t('statsResults.table.player')}
          </TableSortLabel>
        </TableCell>

        <TableCell sx={{ ...headerSx(35), fontWeight: 'bold' }} align="center">
          {t('statsResults.table.position')}
        </TableCell>

        {columns
          .filter((c) => c.id !== 'playerName' && c.id !== 'position')
          .map((col) => (
            <TableCell
              key={col.id}
              sortDirection={orderBy === col.id ? order : false}
              sx={{ ...headerSx(col.width), fontWeight: 'bold' }}
              align="center"
            >
              {!col.sortDisabled ? (
                <TableSortLabel
                  active={orderBy === col.id}
                  direction={orderBy === col.id ? order : 'asc'}
                  onClick={() => handleRequestSort(col.id)}
                  sx={{
                    fontSize: '0.72rem',
                    color: 'white !important',
                    '& .MuiTableSortLabel-icon': {
                      color: 'white !important',
                      opacity: 0.6,
                      visibility: 'visible !important',
                    },
                  }}
                >
                  {col.label}
                </TableSortLabel>
              ) : (
                col.label
              )}
            </TableCell>
          ))}
      </TableRow>
    </TableHead>
  );

  // Cellule "joueur" (avatar + chip ranking + lien)
  const PlayerCell = ({ row, ranking = null, expandable = false, isExpanded = false, onToggleExpand }) => (
    <TableCell
      sx={{
        width: '220px',
        minWidth: '220px',
        maxWidth: '220px',
        padding: '4px 6px',
        textAlign: 'left',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {ranking !== null && orderBy && <PlayerRankingChip ranking={ranking} />}
        <Avatar
          src={row.avatarUrl}
          alt={row.playerName}
          sx={{ width: DESKTOP_AVATAR_SIZE, height: DESKTOP_AVATAR_SIZE, flexShrink: 0 }}
        />
        <Link
          href={`/players/${row.playerId}`}
          target="_blank"
          rel="noopener noreferrer"
          underline="hover"
          color="inherit"
          sx={{
            cursor: 'pointer',
            fontSize: '0.74rem',
            fontWeight: 600,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
            flexGrow: 1,
          }}
        >
          {row.playerName}
        </Link>
        {expandable && (
          <IconButton
            size="small"
            onClick={onToggleExpand}
            sx={{ padding: '2px', flexShrink: 0 }}
          >
            {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
          </IconButton>
        )}
      </Box>
    </TableCell>
  );

  // Rendre une ligne de stats
  function renderStatsRow(row, hideAvatarNamePos = false, rowStyle = {}, ranking = null) {
    return (
      <TableRow
        key={`${row.playerId}-${row.competition_name ?? 'none'}-${row.season_year ?? 'none'}`}
        hover
        sx={rowStyle}
      >
        {!hideAvatarNamePos ? (
          <PlayerCell row={row} ranking={ranking} />
        ) : (
          <TableCell sx={{ width: '220px', padding: '2px' }} />
        )}

        <TableCell sx={cellSx(35)} align="center">
          {!hideAvatarNamePos && (row.position ? translateFromDB(row.position) : '')}
        </TableCell>

        {columns
          .filter((c) => c.id !== 'playerName' && c.id !== 'position')
          .map((col) => {
            const value = row[col.id];
            const bg = getBackgroundColor(col.id, value, row.position);
            if (col.id === 'competition_name') {
              return (
                <TableCell
                  key={col.id}
                  sx={{
                    ...cellSx(col.width),
                    backgroundColor: bg,
                    fontSize: '0.66rem',
                    padding: '2px',
                  }}
                  align="center"
                >
                  <Tooltip title={value || '-'}>
                    <Box
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {value || '-'}
                    </Box>
                  </Tooltip>
                </TableCell>
              );
            }
            return (
              <TableCell
                key={col.id}
                sx={{ ...cellSx(col.width), backgroundColor: bg }}
                align="center"
              >
                {value != null ? value : '-'}
              </TableCell>
            );
          })}
      </TableRow>
    );
  }

  // -------------------------------------------------------------------------
  // MODE GROUPÉ
  // -------------------------------------------------------------------------
  if (isGroupingMode) {
    const paginatedPlayers = statsByPlayer.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );

    return (
      <Box>
        {renderCompareBar()}

        <TableContainer component={Paper} sx={tableContainerSx}>
          <Table sx={tableSx}>
            <DesktopTableHeader />
            <TableBody>
              {paginatedPlayers.map((playerBlock, blockIndex) => {
                const lines = playerBlock.lines;
                const blockBg = blockIndex % 2 === 0 ? '#fff' : '#fafafa';
                const globalRanking = page * rowsPerPage + blockIndex + 1;
                return (
                  <React.Fragment key={playerBlock.playerId}>
                    {lines.map((row, index) => {
                      const isLast = index === lines.length - 1;
                      const rowStyle = {
                        backgroundColor: blockBg,
                        borderBottom: isLast ? '2px solid #d0d0d0' : '1px solid #eee',
                      };
                      return renderStatsRow(
                        row,
                        index > 0,
                        rowStyle,
                        index === 0 ? globalRanking : null
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={statsByPlayer.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage=""
          labelDisplayedRows={({ from, to, count }) =>
            t('statsResults.pagination.displayedRows', { from, to, count })
          }
        />

        {compareDialog}
      </Box>
    );
  }

  // -------------------------------------------------------------------------
  // MODE ACCORDÉON
  // -------------------------------------------------------------------------
  const paginatedStats = sortedStats.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  function getOtherLines(row) {
    const block = statsByPlayer.find((b) => b.playerId === row.playerId);
    if (!block) return [];
    return block.lines.filter(
      (l) =>
        l.season_year !== row.season_year ||
        l.competition_name !== row.competition_name
    );
  }

  return (
    <Box>
      {renderCompareBar()}

      <TableContainer component={Paper} sx={tableContainerSx}>
        <Table sx={tableSx}>
          <DesktopTableHeader />
          <TableBody>
            {paginatedStats.map((row, index) => {
              const isExpanded = !!expandedPlayers[row.playerId];
              const otherLines = getOtherLines(row);
              const globalRanking = page * rowsPerPage + index + 1;

              const rowStyle = {
                backgroundColor: '#fff',
                borderBottom: '1px solid #eee',
              };

              return (
                <React.Fragment
                  key={`${row.playerId}-${row.season_year ?? 'none'}-${row.competition_name ?? 'none'}`}
                >
                  <TableRow hover sx={rowStyle}>
                    <PlayerCell
                      row={row}
                      ranking={globalRanking}
                      expandable={otherLines.length > 0}
                      isExpanded={isExpanded}
                      onToggleExpand={() => handleToggleExpand(row.playerId)}
                    />

                    <TableCell sx={cellSx(35)} align="center">
                      {row.position ? translateFromDB(row.position) : ''}
                    </TableCell>

                    {columns
                      .filter((c) => c.id !== 'playerName' && c.id !== 'position')
                      .map((col) => {
                        const value = row[col.id];
                        const bg = getBackgroundColor(col.id, value, row.position);
                        if (col.id === 'competition_name') {
                          return (
                            <TableCell
                              key={col.id}
                              sx={{
                                ...cellSx(col.width),
                                backgroundColor: bg,
                                fontSize: '0.66rem',
                                padding: '2px',
                              }}
                              align="center"
                            >
                              <Tooltip title={value || '-'}>
                                <Box
                                  sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {value || '-'}
                                </Box>
                              </Tooltip>
                            </TableCell>
                          );
                        }
                        return (
                          <TableCell
                            key={col.id}
                            sx={{ ...cellSx(col.width), backgroundColor: bg }}
                            align="center"
                          >
                            {value != null ? value : '-'}
                          </TableCell>
                        );
                      })}
                  </TableRow>

                  {otherLines.length > 0 &&
                    isExpanded &&
                    otherLines.map((oLine, oIdx) =>
                      renderStatsRow(oLine, true, {
                        backgroundColor: '#f5f5f5',
                        borderBottom:
                          oIdx === otherLines.length - 1
                            ? '2px solid #d0d0d0'
                            : '1px solid #e0e0e0',
                      })
                    )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        rowsPerPageOptions={[10, 25, 50, 100]}
        labelRowsPerPage=""
        labelDisplayedRows={({ from, to, count }) =>
          t('statsResults.pagination.displayedRows', { from, to, count })
        }
      />

      {compareDialog}
    </Box>
  );
};

export default StatsResults;
